import { supabase } from "@/lib/supabase";
import { recordActivity } from "@/services/activityLog";
// Canonical enum source — @myroute/shared mirrors the DB enums from migration
// 0001. Importing from there (instead of redeclaring local unions like the
// old code did) prevents the web client from drifting away from the DB
// schema when the mobile app eventually picks up the same types.
import type { LeadStatus as SharedLeadStatus, RouteStatus as SharedRouteStatus, RouteStopStatus as SharedRouteStopStatus } from "@myroute/shared";

export type RouteStatus = SharedRouteStatus;
export type RouteStopStatus = SharedRouteStopStatus;
export type LeadStatus = SharedLeadStatus;
// VisitEventType is not in shared yet — it's a frontend-only artefact today
// because the apply_route_stop_outcome RPC takes the string and the DB enum
// values are not yet imported into the shared package. Promote when needed.
export type VisitEventType = "arrived" | "completed" | "skipped" | "no_answer" | "sale" | "follow_up" | "note";

export interface RouteRow {
  id: string;
  name: string;
  route_date: string;
  status: RouteStatus;
  assigned_to: string | null;
  assigned_to_name: string | null;
  is_pool: boolean;
  depot_start_id: string | null;
  depot_end_id: string | null;
  total_distance_m: number | null;
  total_duration_s: number | null;
  /** Cached Google encoded-polyline (migration 0009). Null for routes saved
   *  before the cache was wired — Detail.tsx falls back to a live Directions
   *  call in that case. */
  polyline_encoded: string | null;
  optimized_at: string | null;
  created_at: string;
  stop_count: number;
}

export interface RouteStop {
  id: string;
  route_id: string;
  lead_id: string | null;
  sequence: number;
  status: RouteStopStatus;
  distance_from_prev_m: number | null;
  duration_from_prev_s: number | null;
  notes: string | null;
  // joined lead fields:
  lead?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
    phone: string | null;
    phone_alt: string | null;
    email: string | null;
    dob: string | null;
    source: string | null;
    notes: string | null;
    address_street: string | null;
    address_city: string | null;
    address_state: string | null;
    address_zip: string | null;
    address_combined: string | null;
    lat: number | null;
    lng: number | null;
    status: string;
    // jsonb catch-all for everything else the CSV import carried (policy_count,
    // total household annual premium, custom carrier fields, etc.). RLS already
    // grants the assignee SELECT on the full lead row — see
    // 0003_pool_route_lead_visibility.sql.
    custom_fields: Record<string, unknown> | null;
  } | null;
}

export interface RouteDetail extends RouteRow {
  stops: RouteStop[];
}

export interface MappableLead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  phone: string | null;
  status: string;
  lat: number;
  lng: number;
  assigned_to: string | null;
}

/**
 * Lead IDs that are already on an active stop of an active route somewhere in
 * the caller's team — regardless of which driver owns the route. Uses the
 * SECURITY DEFINER RPC `list_team_blocked_lead_ids` from migration 0008
 * because the equivalent direct query would be RLS-scoped (Driver A can't
 * see Driver B's draft route), which previously let two drivers pick the
 * same pool lead onto two different routes. The DB trigger added in the same
 * migration is the hard backstop; this RPC is the UX layer that hides the
 * lead before they ever try.
 */
async function listBlockedLeadIdsOnActiveRoutes(): Promise<Set<string>> {
  const { data, error } = await supabase.rpc("list_team_blocked_lead_ids");
  if (error) throw error;
  const ids = (data ?? []) as (string | null)[];
  const set = new Set<string>();
  for (const id of ids) {
    if (id) set.add(id);
  }
  return set;
}

/**
 * Geocoded leads ELIGIBLE FOR ROUTING — the canonical pool for the planner
 * and the routes map. A lead qualifies when it is:
 *
 *   - not soft-deleted
 *   - geocoded (lat + lng present)
 *   - status NOT IN ('closed', 'do_not_solicit')   — follow_up IS routable
 *   - not currently a pending/arrived stop on a draft / published / in-progress route
 *
 * The two route-stop states (pending, arrived) represent "owed work" on an
 * active route. Once the driver marks a stop completed / no-answer / skipped,
 * the `apply_route_stop_outcome` RPC writes the lead's terminal status, which
 * is what the status filter then catches.
 */
export async function listMappableLeads(): Promise<MappableLead[]> {
  const blockedIds = await listBlockedLeadIdsOnActiveRoutes();

  const { data, error } = await supabase
    .from("leads")
    .select(
      "id, first_name, last_name, full_name, address_street, address_city, address_state, address_zip, phone, status, lat, lng, assigned_to",
    )
    .is("deleted_at", null)
    .not("lat", "is", null)
    .not("lng", "is", null)
    .not("status", "in", "(closed,do_not_solicit)")
    .limit(5000);
  if (error) throw error;
  return (data ?? []).filter((r) => !blockedIds.has(r.id)) as MappableLead[];
}

/**
 * Driver filter values for the admin Routes list. Special tokens "pool" and
 * "unassigned" let an admin slice the list without needing a real user id.
 *   - a uuid string → routes with assigned_to = <uuid> (not pool)
 *   - "pool"        → routes with is_pool = true
 *   - "unassigned"  → routes with assigned_to IS NULL and is_pool = false
 *   - "all" / null  → no filter
 */
export type RouteAssigneeFilter = string | "pool" | "unassigned" | "all" | null;

export interface ListRoutesParams {
  assignedTo?: RouteAssigneeFilter;
  /** Filter to a specific lifecycle status (draft/published/in_progress/...).
   *  Powers the dashboard "Routes in progress" click-through. Omit for all. */
  status?: RouteStatus | null;
}

/**
 * Returns the most recent 500 routes visible to the caller (RLS-scoped).
 * Hard cap avoids silently hitting Supabase's `max_rows` ceiling. If a team
 * ever exceeds this, the All Routes page should grow date/status filters
 * — see TODO in Routes/All.tsx.
 */
export async function listRoutes(params: ListRoutesParams = {}): Promise<RouteRow[]> {
  const { assignedTo, status } = params;
  let query = supabase
    .from("routes")
    .select(
      "id, name, route_date, status, assigned_to, is_pool, depot_start_id, depot_end_id, total_distance_m, total_duration_s, polyline_encoded, optimized_at, created_at, assignee:app_users!routes_assigned_to_fkey(full_name), stops:route_stops(count)",
    )
    .order("route_date", { ascending: false })
    .limit(500);

  if (assignedTo === "pool") {
    query = query.eq("is_pool", true);
  } else if (assignedTo === "unassigned") {
    query = query.is("assigned_to", null).eq("is_pool", false);
  } else if (assignedTo && assignedTo !== "all") {
    query = query.eq("assigned_to", assignedTo).eq("is_pool", false);
  }

  if (status) {
    query = query.eq("status", status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((r) => {
    const assignee = (r as unknown as { assignee: { full_name: string } | null }).assignee;
    const stops = (r as unknown as { stops: { count: number }[] }).stops;
    return {
      id: r.id,
      name: r.name,
      route_date: r.route_date,
      status: r.status as RouteStatus,
      assigned_to: r.assigned_to,
      assigned_to_name: assignee?.full_name ?? null,
      is_pool: !!r.is_pool,
      depot_start_id: r.depot_start_id,
      depot_end_id: r.depot_end_id,
      total_distance_m: r.total_distance_m,
      total_duration_s: r.total_duration_s,
      polyline_encoded: (r as unknown as { polyline_encoded: string | null }).polyline_encoded ?? null,
      optimized_at: r.optimized_at,
      created_at: r.created_at,
      stop_count: stops?.[0]?.count ?? 0,
    } satisfies RouteRow;
  });
}

export async function getRoute(id: string): Promise<RouteDetail | null> {
  const { data, error } = await supabase
    .from("routes")
    .select(
      "id, name, route_date, status, assigned_to, is_pool, depot_start_id, depot_end_id, total_distance_m, total_duration_s, polyline_encoded, optimized_at, created_at, assignee:app_users!routes_assigned_to_fkey(full_name)",
    )
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const { data: stops, error: serr } = await supabase
    .from("route_stops")
    .select(
      "id, route_id, lead_id, sequence, status, distance_from_prev_m, duration_from_prev_s, notes, lead:leads(id, first_name, last_name, full_name, phone, phone_alt, email, dob, source, notes, address_street, address_city, address_state, address_zip, address_combined, lat, lng, status, custom_fields)",
    )
    .eq("route_id", id)
    .order("sequence", { ascending: true });
  if (serr) throw serr;

  const assignee = (data as unknown as { assignee: { full_name: string } | null }).assignee;
  return {
    id: data.id,
    name: data.name,
    route_date: data.route_date,
    status: data.status as RouteStatus,
    assigned_to: data.assigned_to,
    assigned_to_name: assignee?.full_name ?? null,
    is_pool: !!data.is_pool,
    depot_start_id: data.depot_start_id,
    depot_end_id: data.depot_end_id,
    total_distance_m: data.total_distance_m,
    total_duration_s: data.total_duration_s,
    polyline_encoded: (data as unknown as { polyline_encoded: string | null }).polyline_encoded ?? null,
    optimized_at: data.optimized_at,
    created_at: data.created_at,
    stop_count: stops?.length ?? 0,
    stops: (stops ?? []) as unknown as RouteStop[],
  };
}

export interface CreateRouteInput {
  name: string;
  route_date: string;
  depot_start_id: string | null;
  depot_end_id: string | null;
  /** When `is_pool` is true this is ignored. */
  assigned_to: string | null;
  is_pool: boolean;
  total_distance_m: number;
  /** Optional but strongly preferred: the encoded polyline + duration from
   *  the Google Routes call the planner already made. Persists the geometry
   *  so future Detail-page renders skip the Directions API entirely. */
  total_duration_s?: number;
  polyline_encoded?: string | null;
  stops: Array<{
    lead_id: string;
    sequence: number;
    distance_from_prev_m: number;
  }>;
}

export async function createRoute(input: CreateRouteInput, _ctx: { teamId: string; userId: string }): Promise<string> {
  // Delegate to the atomic create_split_routes RPC (migration 0024) with a
  // single-element array. The old two-step insert (route row, then stops)
  // could orphan a stop-less route when the stops batch failed and the
  // best-effort cleanup delete also failed. The RPC is one transaction:
  // team_id/created_by are stamped server-side, status/pool semantics match.
  const { data, error } = await supabase.rpc("create_split_routes", {
    p_routes: [
      {
        name: input.name,
        route_date: input.route_date,
        depot_start_id: input.depot_start_id,
        depot_end_id: input.depot_end_id,
        assigned_to: input.is_pool ? null : input.assigned_to,
        is_pool: input.is_pool,
        total_distance_m: input.total_distance_m,
        total_duration_s: input.total_duration_s ?? null,
        polyline_encoded: input.polyline_encoded ?? null,
        stops: input.stops,
      },
    ],
  });
  if (error) throw error;
  const routeId = (data as string[] | null)?.[0];
  if (!routeId) throw new Error("create_split_routes returned no route id");
  void recordActivity({
    kind: "route_create",
    target_type: "route",
    target_id: routeId,
    summary: `Created route "${input.name}" with ${input.stops.length} stops`,
    metadata: {
      route_date: input.route_date,
      is_pool: input.is_pool,
      assigned_to: input.is_pool ? null : input.assigned_to,
      stop_count: input.stops.length,
      total_distance_m: input.total_distance_m,
    },
  });
  return routeId;
}

export async function deleteRoute(id: string): Promise<void> {
  // Read the name BEFORE deleting so the activity-log line is human-readable.
  const { data: row } = await supabase.from("routes").select("name").eq("id", id).maybeSingle();
  const { error } = await supabase.from("routes").delete().eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "route_delete",
    target_type: "route",
    target_id: id,
    summary: row?.name ? `Deleted route "${row.name}"` : "Deleted route",
  });
}

/**
 * Marks a route `cancelled` without deleting it. The trigger from migration
 * 0008 treats `cancelled` as terminal, so all its stops immediately stop
 * blocking their leads — the leads return to the routable pool.
 *
 * Soft alternative to deleteRoute: history is preserved for visit_events /
 * lead_status_history audit trails.
 */
export async function cancelRoute(id: string): Promise<void> {
  const { data: row } = await supabase.from("routes").select("name").eq("id", id).maybeSingle();
  const { error } = await supabase
    .from("routes")
    .update({ status: "cancelled", updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "route_cancel",
    target_type: "route",
    target_id: id,
    summary: row?.name ? `Cancelled route "${row.name}"` : "Cancelled route",
  });
}

/** Atomic claim of a pool route. Server-side enforces is_pool=true. */
export async function claimRoute(id: string): Promise<void> {
  const { error } = await supabase.rpc("claim_route", { p_route_id: id });
  if (error) throw error;
}

/**
 * Reassigns a route to a member, or sends it to the team pool.
 *   assignee = <user uuid> → direct assignment (un-pools if it was pooled)
 *   assignee = "pool"      → unassigned pool route, published so drivers can claim
 * Elevated roles only via routes_update RLS.
 */
export async function reassignRoute(id: string, assignee: string | "pool"): Promise<void> {
  const { data: row } = await supabase.from("routes").select("name, status").eq("id", id).maybeSingle();
  const toPool = assignee === "pool";
  const patch: Record<string, unknown> = {
    assigned_to: toPool ? null : assignee,
    is_pool: toPool,
    updated_at: new Date().toISOString(),
  };
  // Pool routes must be visible/claimable; a draft in the pool is invisible
  // to drivers. Direct reassignment keeps the current lifecycle status.
  if (toPool && row?.status === "draft") patch.status = "published";
  const { error } = await supabase.from("routes").update(patch).eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "route_reassign",
    target_type: "route",
    target_id: id,
    summary: toPool
      ? `Sent route "${row?.name ?? id}" to the team pool`
      : `Reassigned route "${row?.name ?? id}"`,
    metadata: { assignee: toPool ? null : assignee, to_pool: toPool },
  });
}

// ---------- Territory splitter: atomic K-route create -----------------------

export interface SplitRoutePayload {
  name: string;
  route_date: string;
  depot_start_id: string | null;
  depot_end_id: string | null;
  assigned_to: string | null;
  is_pool: boolean;
  total_distance_m: number;
  total_duration_s?: number | null;
  polyline_encoded?: string | null;
  stops: Array<{ lead_id: string; sequence: number; distance_from_prev_m: number }>;
}

/**
 * Creates K routes atomically via the migration-0024 `create_split_routes`
 * RPC. Used by the territory splitter (compass-sector or zip-set, dispatching
 * to N drivers). All K either commit or all fail — partial states never
 * reach the database, so admins won't end up with "3 of 4 drivers got their
 * slice and the 4th doesn't exist."
 *
 * Returns the created route ids in the same order as the input payload.
 */
export async function createSplitRoutes(routes: SplitRoutePayload[]): Promise<string[]> {
  if (routes.length === 0) return [];
  const { data, error } = await supabase.rpc("create_split_routes", { p_routes: routes });
  if (error) throw error;
  // record_activity per route would be ideal but the RPC is one transaction
  // — firing N activity entries from inside it is doable but adds noise.
  // Log a single split-event from the client instead.
  void recordActivity({
    kind: "route_create",
    summary: `Split ${routes.length} routes across drivers (${routes.reduce((n, r) => n + r.stops.length, 0)} total stops)`,
    metadata: {
      split: true,
      route_count: routes.length,
      total_stops: routes.reduce((n, r) => n + r.stops.length, 0),
    },
  });
  return (data ?? []) as string[];
}

// ---------- Edit saved-route stops (Phase 2D) -------------------------------

/**
 * Clears the cached geometry on a route. Used whenever a stop is added or
 * removed — the saved polyline was computed for the OLD stop set, so leaving
 * it would render a polyline that doesn't match the visible numbered pins.
 * Detail.tsx falls back to a live Google Routes call when polyline_encoded
 * is null, so the next view re-derives the geometry correctly.
 *
 * Also nulls total_distance_m and total_duration_s for the same reason — the
 * route header would otherwise show stale "12.4 mi / 34 min" figures.
 */
async function invalidateRouteGeometryCache(routeId: string): Promise<void> {
  const { error } = await supabase
    .from("routes")
    .update({
      polyline_encoded: null,
      total_distance_m: null,
      total_duration_s: null,
      optimized_at: null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", routeId);
  if (error) throw error;
}

/**
 * Removes a stop from a saved route, then renumbers the trailing stops so
 * `sequence` stays dense (1..N). The double-booking trigger checks lead_id
 * + status, not sequence, so a bulk UPDATE of sequence numbers is safe — no
 * trigger will fire mid-renumber.
 *
 * Geometry cache is invalidated last so the Detail page recomputes the
 * polyline + leg distances against the new stop set on next view.
 *
 * Refuses to remove stops in terminal status (completed/skipped/no_answer):
 * those rows are audit history. The DB has no constraint for this — the UI
 * just hides the button — but the service layer enforces it too as a backstop
 * in case a curl client tries to slip one through.
 */
export async function removeRouteStop(stopId: string): Promise<void> {
  const { data: stop, error: readErr } = await supabase
    .from("route_stops")
    .select("id, route_id, sequence, status")
    .eq("id", stopId)
    .maybeSingle();
  if (readErr) throw readErr;
  if (!stop) throw new Error("Stop not found");
  if (stop.status !== "pending" && stop.status !== "arrived") {
    throw new Error("Completed or skipped stops can't be removed — they're part of the route's history.");
  }

  const { error: delErr } = await supabase.from("route_stops").delete().eq("id", stopId);
  if (delErr) throw delErr;
  void recordActivity({
    kind: "route_stop_remove",
    target_type: "route",
    target_id: stop.route_id as string,
    summary: `Removed stop #${stop.sequence} from route`,
    metadata: { stop_id: stopId, sequence: stop.sequence },
  });

  // Renumber trailing stops down by one to keep sequence dense. Done as a
  // single update with a where-clause; if it half-completes the gap is purely
  // cosmetic and a future Plan-page save would fix it anyway.
  const { data: trailing, error: trailErr } = await supabase
    .from("route_stops")
    .select("id, sequence")
    .eq("route_id", stop.route_id)
    .gt("sequence", stop.sequence);
  if (trailErr) throw trailErr;
  for (const row of trailing ?? []) {
    const { error: uErr } = await supabase
      .from("route_stops")
      .update({ sequence: (row.sequence as number) - 1 })
      .eq("id", row.id);
    if (uErr) throw uErr;
  }

  await invalidateRouteGeometryCache(stop.route_id);
}

/**
 * Appends a lead to the end of an existing route. Picks the next sequence
 * number atomically by reading max(sequence) first — small race window vs
 * a concurrent add, but the unique (route_id, sequence) constraint would
 * make the second insert fail loudly rather than silently corrupt.
 *
 * The double_book trigger from migration 0008 enforces lead-uniqueness across
 * the team's active routes; this function does no extra check — the trigger's
 * 23505 will bubble up as a normal error if the lead is already booked.
 */
export async function appendRouteStop(routeId: string, leadId: string): Promise<void> {
  const { data: maxRow, error: maxErr } = await supabase
    .from("route_stops")
    .select("sequence")
    .eq("route_id", routeId)
    .order("sequence", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (maxErr) throw maxErr;
  const nextSeq = ((maxRow?.sequence as number | undefined) ?? 0) + 1;

  const { error: insErr } = await supabase.from("route_stops").insert({
    route_id: routeId,
    lead_id: leadId,
    sequence: nextSeq,
    // distance_from_prev_m is null on the freshly added stop — gets back-filled
    // the next time the route is re-optimized (and the cleared polyline cache
    // forces that recompute on the next Detail view).
  });
  if (insErr) throw insErr;
  void recordActivity({
    kind: "route_stop_add",
    target_type: "route",
    target_id: routeId,
    summary: `Added stop #${nextSeq} to route`,
    metadata: { lead_id: leadId, sequence: nextSeq },
  });

  await invalidateRouteGeometryCache(routeId);
}

/**
 * Atomic outcome write via DB RPC (stop + lead + visit_event + route).
 */
export async function updateRouteStopStatus(
  stopId: string,
  nextStatus: RouteStopStatus,
  opts: { notes?: string; leadStatus: LeadStatus; eventType: VisitEventType },
): Promise<void> {
  const { error } = await supabase.rpc("apply_route_stop_outcome", {
    p_stop_id: stopId,
    p_stop_status: nextStatus,
    p_lead_status: opts.leadStatus,
    p_event_type: opts.eventType,
    p_notes: opts.notes ?? null,
  });
  if (error) throw error;
}
